import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

export interface CascadeSubstep {
  label: string;
  status: string;
  timestamp?: string;
  isActive?: boolean;
}

interface ToneStyle {
  bg: string;
  ring: string;
  badge: string;
  label: string;
}

interface EnrichedSubstep extends CascadeSubstep {
  tone: ToneStyle;
}

interface Signature {
  Args: {
    label: string;
    status: string;
    summary?: string; // "2 succeeded · 2 skipped" — shown on group header
    timestamp?: string; // "08:23:10 CST" — shown on leaf row
    substeps?: CascadeSubstep[];
    isActive?: boolean;
    defaultOpen?: boolean;
  };
  Element: HTMLDivElement;
}

function toneFor(status: string): ToneStyle {
  switch ((status ?? '').toUpperCase()) {
    case 'SUCCESS':
      return {
        bg: 'bg-cilantro-50',
        ring: 'ring-cilantro-50',
        badge: 'bg-cilantro-10 text-cilantro-60',
        label: 'Success',
      };
    case 'FAILURE':
      return { bg: 'bg-papaya-50', ring: 'ring-papaya-50', badge: 'bg-papaya-10 text-papaya-60', label: 'Failure' };
    case 'PARTIAL_FAILURE':
      return { bg: 'bg-amber-500', ring: 'ring-amber-500', badge: 'bg-amber-100 text-amber-700', label: 'Partial' };
    case 'SKIPPED':
    case 'SKIPPED_NO_MATCH':
      return { bg: 'bg-carbon-30', ring: 'ring-carbon-30', badge: 'bg-gray-100 text-carbon-40', label: 'Skipped' };
    case 'CANCELLED':
      return { bg: 'bg-carbon-30', ring: 'ring-carbon-30', badge: 'bg-gray-100 text-carbon-40', label: 'Cancelled' };
    default:
      return { bg: 'bg-carbon-30', ring: 'ring-carbon-30', badge: 'bg-gray-100 text-carbon-40', label: status ?? '—' };
  }
}

export default class CascadeStepGroup extends Component<Signature> {
  @tracked isOpen = this.args.defaultOpen ?? false;

  @action toggle(): void {
    this.isOpen = !this.isOpen;
  }

  get hasChildren(): boolean {
    return (this.args.substeps?.length ?? 0) > 0;
  }

  get tone(): ToneStyle {
    return toneFor(this.args.status);
  }

  get enrichedSubsteps(): EnrichedSubstep[] {
    return (this.args.substeps ?? []).map((s) => ({ ...s, tone: toneFor(s.status) }));
  }

  <template>
    <div
      class="rounded-xl border overflow-hidden {{if @isActive 'border-gem-40 bg-gem-5' 'border-carbon-10'}}"
      ...attributes
    >
      {{#if this.hasChildren}}
        {{! ── Group header (expandable) ── }}
        <button
          type="button"
          class="flex items-center gap-2.5 w-full px-5 py-3 text-left transition-colors focus:outline-none
            {{if @isActive 'bg-gem-5 hover:bg-gem-10' 'bg-carbon-5 hover:bg-carbon-10'}}"
          {{on "click" this.toggle}}
        >
          <span class="w-3 h-3 shrink-0 flex items-center justify-center text-carbon-50">
            {{svgJar
              "carot-right"
              height="10"
              width="10"
              class=(if this.isOpen "rotate-90 transition-transform duration-150" "transition-transform duration-150")
            }}
          </span>
          <span
            class="w-4 h-4 rounded-full outline outline-1 outline-cilantro-50 bg-transparent shrink-0 flex items-center justify-center"
          >
            <span class="rounded-full bg-cilantro-50" style="width:13px;height:13px;"></span>
          </span>
          <span class="text-sm font-bold text-carbon-90">{{@label}}</span>
          <span class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full {{this.tone.badge}}">
            {{this.tone.label}}
          </span>
          {{#if @summary}}
            <span class="ml-auto text-xs text-carbon-50 whitespace-nowrap pl-2">{{@summary}}</span>
          {{/if}}
        </button>

        {{! ── Substeps (shown when expanded) ── }}
        {{#if this.isOpen}}
          <div class="flex flex-col">
            {{#each this.enrichedSubsteps as |step|}}
              <div
                class="flex items-center gap-2.5 px-5 py-2.5 border-t border-carbon-10 {{if step.isActive 'bg-gem-5'}}"
              >
                <span class="w-3 h-3 shrink-0" aria-hidden="true"></span>
                <span
                  class="w-4 h-4 rounded-full outline outline-1 outline-cilantro-50 bg-transparent shrink-0 flex items-center justify-center"
                >
                  <span class="rounded-full bg-cilantro-50" style="width:10px;height:10px;"></span>
                </span>
                <span class="text-sm font-semibold text-carbon-80">{{step.label}}</span>
                <span class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full {{step.tone.badge}}">
                  {{step.tone.label}}
                </span>
                {{#if step.timestamp}}
                  <span class="ml-auto text-xs text-carbon-50 tabular-nums pl-2">{{step.timestamp}}</span>
                {{/if}}
              </div>
            {{/each}}
          </div>
        {{/if}}

      {{else}}
        {{! ── Leaf row (no children) ── }}
        <div class="flex items-center gap-2.5 px-5 py-3">
          {{! Spacer aligns label with group-header labels (which have a chevron) }}
          <span class="w-3 h-3 shrink-0" aria-hidden="true"></span>
          {{! leaf node indicator }}
          <span
            class="w-4 h-4 rounded-full outline outline-1 outline-cilantro-50 bg-transparent shrink-0 flex items-center justify-center"
          >
            <span class="rounded-full bg-cilantro-50" style="width:10px;height:10px;"></span>
          </span>
          <span class="text-sm font-bold text-carbon-90">{{@label}}</span>
          <span class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full {{this.tone.badge}}">
            {{this.tone.label}}
          </span>
          {{#if @timestamp}}
            <span class="ml-auto text-xs text-carbon-50 tabular-nums pl-2">{{@timestamp}}</span>
          {{/if}}
        </div>
      {{/if}}
    </div>
  </template>
}
